// client/src/components/leagues/PlayoffsTab.js
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import BracketDiagram from '../bracket/BracketDiagram'
import ScoreReportingModal from '../bracket/ScoreReportingModal'
import ApiPlayoffs from '../../api/ApiPlayoffs'
import toastr from 'toastr'
import { MATCH_STATUS, MATCH_OUTCOMES } from '../../utils/matchStatus'
import useAuth from '../../hooks/useAuth'
import { getActiveLevels } from '../../utils/leagueLevels'

// Auto-refresh interval in milliseconds (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000

/**
 * PlayoffsTab component displays playoff information and interactive bracket
 * for users when the league is in playoffs status
 */
export default function PlayoffsTab({ leagueId, league, leagueLevelId }) {
  const navigate = useNavigate()
  const { isAdmin, user } = useAuth()
  const activeLevels = getActiveLevels(league)
  const hasLevelTabs = activeLevels.length > 1
  const [bracket, setBracket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedLevelId, setSelectedLevelId] = useState(
    activeLevels[0]?.id ?? null,
  )

  // Interactive bracket state
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false)
  const [collapsedRounds, setCollapsedRounds] = useState(new Set())
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true)
  const refreshIntervalRef = useRef(null)

  // Get current player ID from auth context
  const currentPlayerId = user?.primary_player_id || null
  const selectedLevel =
    activeLevels.find((level) => level.id === selectedLevelId) || null
  // When league_levels are loaded, use the selected tab's level; otherwise fall
  // back to the player's own level (from their division) so tiered brackets are
  // always scoped correctly even when league data doesn't include league_levels.
  const effectiveLeagueLevelId = hasLevelTabs
    ? selectedLevelId
    : (leagueLevelId ?? null)

  useEffect(() => {
    if (activeLevels.length === 0) {
      setSelectedLevelId(null)
      return
    }

    if (!activeLevels.some((level) => level.id === selectedLevelId)) {
      setSelectedLevelId(activeLevels[0].id)
    }
  }, [activeLevels, selectedLevelId])

  const loadBracketData = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) {
          setLoading(true)
        }
        setError(null)

        const result = await ApiPlayoffs.getBracket(
          leagueId,
          effectiveLeagueLevelId,
        )
        // Extract the bracket property from the API response
        setBracket(result.bracket)
      } catch (err) {
        console.error('Failed to load bracket data:', err)
        setError(err.message || 'Failed to load playoff bracket')
        if (showLoading) {
          toastr.error('Failed to load playoff bracket')
        }
      } finally {
        if (showLoading) {
          setLoading(false)
        }
      }
    },
    [effectiveLeagueLevelId, leagueId],
  )

  // Initial load
  useEffect(() => {
    if (leagueId && (!hasLevelTabs || effectiveLeagueLevelId !== null)) {
      loadBracketData()
    }
  }, [effectiveLeagueLevelId, hasLevelTabs, leagueId, loadBracketData])

  // Auto-refresh effect
  // Note: loadBracketData is intentionally omitted from deps to prevent interval recreation
  // The function is stable enough via useCallback with leagueId dependency
  useEffect(() => {
    if (
      isAutoRefreshEnabled &&
      leagueId &&
      !isScoreModalOpen &&
      (!hasLevelTabs || effectiveLeagueLevelId !== null)
    ) {
      refreshIntervalRef.current = setInterval(() => {
        loadBracketData(false) // Silent refresh without loading indicator
      }, AUTO_REFRESH_INTERVAL)
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [
    effectiveLeagueLevelId,
    hasLevelTabs,
    isAutoRefreshEnabled,
    leagueId,
    isScoreModalOpen,
    loadBracketData,
  ])

  // Handle match click for score reporting
  const handleMatchClick = useCallback(
    (match) => {
      // Check if user can report scores for this match
      const isParticipant =
        currentPlayerId &&
        (match.home_player_id === currentPlayerId ||
          match.away_player_id === currentPlayerId)

      if (!isParticipant && !isAdmin) {
        toastr.info('Only match participants or admins can report scores')
        return
      }

      setSelectedMatch(match)
      setIsScoreModalOpen(true)
    },
    [currentPlayerId, isAdmin],
  )

  // Handle score submission
  const handleScoreSubmit = useCallback(
    async (matchId, setScores, options) => {
      try {
        await ApiPlayoffs.reportResult(matchId, setScores, options)
        toastr.success('Match score reported successfully!')
        // Reload bracket data to reflect changes
        await loadBracketData(false)
      } catch (err) {
        console.error('Failed to report score:', err)
        toastr.error(err.message || 'Failed to report match score')
        throw err // Re-throw to let modal handle it
      }
    },
    [loadBracketData],
  )

  // Handle round collapse toggle
  const handleToggleRound = useCallback((roundNumber) => {
    setCollapsedRounds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(roundNumber)) {
        newSet.delete(roundNumber)
      } else {
        newSet.add(roundNumber)
      }
      return newSet
    })
  }, [])

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback(() => {
    setIsAutoRefreshEnabled((prev) => !prev)
  }, [])

  // Manual refresh
  const handleManualRefresh = useCallback(() => {
    loadBracketData(false)
    toastr.info('Bracket refreshed')
  }, [loadBracketData])

  const getCurrentStage = () => {
    if (!bracket?.rounds?.length) return 'Playoffs'

    const totalRounds = bracket.rounds.length

    // Find the current active round (first round with incomplete matches)
    for (let i = 0; i < bracket.rounds.length; i++) {
      const round = bracket.rounds[i]
      const hasIncompleteMatch = round.matches?.some(
        (match) =>
          match.status === MATCH_STATUS.CREATED ||
          match.status === MATCH_STATUS.SCHEDULED,
      )

      if (hasIncompleteMatch) {
        return getRoundName(i + 1, totalRounds)
      }
    }

    // All matches complete
    return 'Finals Complete'
  }

  const getRoundName = (roundNumber, totalRounds) => {
    if (totalRounds === 1) return 'Finals'
    if (roundNumber === totalRounds) return 'Finals'
    if (roundNumber === totalRounds - 1) return 'Semifinals'
    if (roundNumber === totalRounds - 2) return 'Quarterfinals'
    return `Round ${roundNumber}`
  }

  const getPlayoffRules = () => {
    return (
      league?.rules ||
      'Standard playoff rules apply. Single elimination format.'
    )
  }

  const getPlayoffStatus = () => {
    if (!bracket?.rounds?.length) {
      return 'Playoff bracket is being prepared'
    }

    const allMatches = bracket.rounds.flatMap((round) => round.matches || [])
    const completedMatches = allMatches.filter((match) =>
      MATCH_OUTCOMES.includes(match.status),
    )
    const scheduledMatches = allMatches.filter(
      (match) => match.status === MATCH_STATUS.SCHEDULED,
    )
    const pendingMatches = allMatches.filter(
      (match) => match.status === MATCH_STATUS.CREATED,
    )

    if (completedMatches.length === allMatches.length) {
      return 'Tournament complete!'
    }

    return `${completedMatches.length}/${allMatches.length} matches completed • ${scheduledMatches.length} scheduled • ${pendingMatches.length} pending`
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading playoff bracket...</span>
        </div>
        <p className="mt-2 text-muted">Loading playoff bracket...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
        <h5 className="text-muted">Unable to load playoff bracket</h5>
        <p className="text-muted">{error}</p>
        <button className="btn btn-outline-primary" onClick={loadBracketData}>
          <i className="fas fa-redo me-2"></i>
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="playoffs-tab">
      {/* Playoffs Header */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h3 className="mb-1">
                <i className="fas fa-trophy text-warning me-2"></i>
                Playoffs
              </h3>
              <p className="text-muted mb-0">
                Single elimination tournament
                {selectedLevel ? ` • Level ${selectedLevel.skill_level}` : ''}
              </p>
            </div>
            <span className="badge bg-purple fs-6">{getCurrentStage()}</span>
          </div>

          {hasLevelTabs && (
            <div className="row mb-3">
              <div className="col-12">
                <ul className="nav nav-tabs">
                  {activeLevels.map((level) => (
                    <li className="nav-item" key={level.id}>
                      <button
                        type="button"
                        className={`nav-link ${
                          selectedLevelId === level.id ? 'active' : ''
                        }`}
                        data-testid={`playoff-level-tab-${level.id}`}
                        onClick={() => setSelectedLevelId(level.id)}
                      >
                        {level.skill_level}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Current Status */}
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-muted mb-2">
                <i className="fas fa-info-circle me-1"></i>
                Tournament Status
              </h6>
              <p className="mb-3">{getPlayoffStatus()}</p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-2">
                <i className="fas fa-gavel me-1"></i>
                Rules
              </h6>
              <p className="mb-3">{getPlayoffRules()}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/leagues/${leagueId}/standings`)}
            >
              <i className="fas fa-list me-2"></i>
              View Standings
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate(`/leagues/${leagueId}/matches`)}
            >
              <i className="fas fa-tennis-ball me-2"></i>
              View Matches
            </button>
          </div>
        </div>
      </div>

      {/* Interactive Bracket */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-sitemap me-2"></i>
            Tournament Bracket
            {selectedLevel ? (
              <span className="text-muted fs-6 ms-2">
                Level {selectedLevel.skill_level}
              </span>
            ) : null}
          </h5>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleManualRefresh}
              title="Refresh bracket"
              aria-label="Refresh bracket"
            >
              <span aria-hidden="true" className="me-1">
                ↻
              </span>
              Refresh
            </button>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="autoRefreshToggle"
                checked={isAutoRefreshEnabled}
                onChange={toggleAutoRefresh}
              />
              <label
                className="form-check-label small"
                htmlFor="autoRefreshToggle"
              >
                Auto-refresh
              </label>
            </div>
          </div>
        </div>
        <div className="card-body">
          {!bracket?.rounds?.length ? (
            <div className="text-center py-5">
              <i className="fas fa-trophy fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">Bracket Not Available</h5>
              <p className="text-muted">
                The tournament bracket will appear here once it has been
                finalized.
              </p>
            </div>
          ) : (
            <div>
              <div
                className="bracket-scroll-container"
                style={{
                  overflow: 'auto',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                }}
              >
                <BracketDiagram
                  bracket={bracket}
                  simpleScroll={true}
                  className="user-bracket"
                  onMatchClick={handleMatchClick}
                  collapsedRounds={collapsedRounds}
                  onToggleRound={handleToggleRound}
                />
              </div>
              <div className="text-center mt-3">
                <p className="text-muted small mb-0">
                  <i className="fas fa-hand-pointer me-1"></i>
                  Click on a ready match to report scores • Click round headers
                  to expand/collapse
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Score Reporting Modal */}
      <ScoreReportingModal
        isOpen={isScoreModalOpen}
        onClose={() => {
          setIsScoreModalOpen(false)
          setSelectedMatch(null)
        }}
        match={selectedMatch}
        onSubmit={handleScoreSubmit}
        isAdmin={isAdmin}
        currentPlayerId={currentPlayerId}
        matchFormat={league?.match_format}
      />
    </div>
  )
}
